/** Clamp to [0, 1]. */
function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

function parseHexRgb(hex) {
  const raw = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

function rgbToHex(r, g, b) {
  const to = (n) => Math.round(clamp01(n / 255) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

function hslToRgb(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return [(rp + m) * 255, (gp + m) * 255, (bp + m) * 255];
}

/** Hue fan around parent — keeps fuel family, separates neighbors. */
export function hueFanColor(parentHex, index, count) {
  const rgb = parseHexRgb(parentHex);
  if (!rgb) return parentHex || '#7F8C8D';
  if (count <= 1) return parentHex;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const mid = (count - 1) / 2;
  const span = Math.min(28, 8 + count * 4);
  const hue = h + ((index - mid) / Math.max(mid, 1)) * span;
  const light = clamp01(l + (index - mid) * 0.03);
  const sat = clamp01(s * 0.95 + 0.05);
  const [nr, ng, nb] = hslToRgb(hue, sat, light);
  return rgbToHex(nr, ng, nb);
}

/** Hue fan + lightness by fixed slot index (not runtime merit). */
function huePlusMeritColor(parentHex, index, count) {
  const fan = hueFanColor(parentHex, index, count);
  const rgb = parseHexRgb(fan);
  if (!rgb || count <= 1) return fan;
  const t = index / (count - 1);
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const light = clamp01(l * 0.75 + t * 0.28);
  const [nr, ng, nb] = hslToRgb(h, s, light);
  return rgbToHex(nr, ng, nb);
}

/**
 * Catalog may overlay display names and parent fuel; extra tech_ids use a hash fallback.
 */
const VO_FLEX_COLOR_SLOTS = {
  bio: ['bio_power'],
  coal: ['subcritical_coal', 'supercritical_coal', 'coal_ccs_retrofit', 'coal_ccs'],
  gas: ['ccgt_gas', 'ocgt_peaker', 'gas_ccs_retrofit', 'gas_ccs', 'int_comb_gas'],
  oil: ['ccgt_oil', 'gas_oil_steam', 'int_comb_oil'],
};

function voFlexColorSlotIndex(fuel, techId) {
  const slots = VO_FLEX_COLOR_SLOTS[fuel] || [];
  if (!slots.length) return { index: 0, count: 1 };
  const id = String(techId || '');
  let index = id ? slots.indexOf(id) : -1;
  if (index < 0) {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    index = h % slots.length;
  }
  return { index, count: slots.length };
}

/**
 * Sub-fuel color: hue fan + catalog-slot lightness (approach D look),
 * keyed by catalog tech_id — independent of model merit order.
 */
export function voFlexSubfuelColor(parentHex, fuel, techId) {
  const { index, count } = voFlexColorSlotIndex(fuel, techId);
  return huePlusMeritColor(parentHex, index, count);
}

export const VO_FLEX_CHART_FUELS = [
  { fuel: 'bio', parentId: 'bio_dispatch', parentName: 'Bio (dispatch)', colorKey: 'bioenergy' },
  { fuel: 'coal', parentId: 'coal_dispatch', parentName: 'Coal (dispatch)', colorKey: 'coal' },
  { fuel: 'gas', parentId: 'gas', parentName: 'Gas', colorKey: 'gas' },
  { fuel: 'oil', parentId: 'oil', parentName: 'Oil', colorKey: 'oil' },
];
